import { FC, PropsWithChildren, useEffect } from 'react';

import {
  logError,
  logWarning,
} from '@savgroup-front-common/configuration/appInsights/AppInsights';

import { toast } from '../../atoms/Toast';
import createGenericContext from '../../helpers/createGenericContext';

import { useNetworkQuality } from './hooks/useNetworkQuality';
import messages from './messages';
import { NETWORK_TOAST_ID } from './NetworkStatusProvider.constants';
import { NETWORK_STATUS, NetworkStatus } from './NetworkStatusProvider.types';

export interface NetworkStatusContextValue {
  networkStatus: NetworkStatus;
}

const [useNetworkStatus, NetworkStatusContextProvider] =
  createGenericContext<NetworkStatusContextValue>();

const NetworkStatusProvider: FC<PropsWithChildren> = ({ children }) => {
  const { networkStatus, networkContextRef } = useNetworkQuality();

  useEffect(() => {
    return () => {
      toast.dismiss(NETWORK_TOAST_ID);
    };
  }, []);

  useEffect(() => {
    toast.dismiss(NETWORK_TOAST_ID);

    if (networkStatus === NETWORK_STATUS.GOOD) {
      return;
    }

    if (networkStatus === NETWORK_STATUS.OFFLINE) {
      toast.error(messages.offlineBody, {
        autoClose: false,
        toastId: NETWORK_TOAST_ID,
        title: messages.offlineTitle,
      });
      logError('Network status: offline detected', {
        ...networkContextRef.current,
        networkStatus,
      });

      return;
    }

    const isCypress = 'Cypress' in window;

    if (!isCypress) {
      toast.warn(messages.degradedBody, {
        autoClose: false,
        toastId: NETWORK_TOAST_ID,
        title: messages.degradedTitle,
      });
    }

    logWarning('Network status: degraded connection detected', {
      ...networkContextRef.current,
      networkStatus,
      isCypress,
    });
  }, [networkStatus, networkContextRef]);

  return (
    <NetworkStatusContextProvider value={{ networkStatus }}>
      {children}
    </NetworkStatusContextProvider>
  );
};

export { NetworkStatusProvider, useNetworkStatus };
